// TEMPORÁRIO: mudar para false e republicar antes do lançamento.
export const DEMO_AVAILABLE = true;
export const DEMO_ROLE_KEY = 'bbqjobs:demo:v1:role';
export const DEMO_DATA_KEY = 'bbqjobs:demo:v1:data';
export type DemoRole = 'trabalhador' | 'restaurante';
export function getDemoRole(storage?: Pick<Storage, 'getItem'>): DemoRole | null {
  if (!DEMO_AVAILABLE) return null;
  try {
    const value = (storage || window.sessionStorage).getItem(DEMO_ROLE_KEY);
    return value === 'trabalhador' || value === 'restaurante' ? value : null;
  } catch { return null; }
}
export function enterDemo(role: DemoRole) {
  if (!DEMO_AVAILABLE || !['trabalhador', 'restaurante'].includes(role)) throw new Error('Demonstração indisponível.');
  window.sessionStorage.setItem(DEMO_ROLE_KEY, role);
  window.location.assign('/perfil');
}
export function exitDemo() {
  window.sessionStorage.removeItem(DEMO_ROLE_KEY);
  window.location.assign('/cadastro');
}
export function resetDemo() {
  window.sessionStorage.removeItem(DEMO_DATA_KEY);
  window.location.reload();
}
const ids = { trabalhador: 'demo-trabalhador', restaurante: 'demo-estabelecimento' };
const names = { trabalhador: 'Alex — Demonstração', restaurante: 'Brasa — Estabelecimento fictício' };
const clone = <T>(value: T): T => structuredClone(value);
function seed() {
  const now = new Date().toISOString();
  const common = {createdAt: now, updatedAt: now};
  return {
    user_profiles: (['trabalhador', 'restaurante'] as const).map(role => ({...common, _id: `profile-${ids[role]}`, userId: ids[role], creator: ids[role], nome: names[role], userName: names[role], profileType: role, cidade: 'Milão — demonstração', biografia: 'Perfil fictício para testar o BBQ Jobs.', profileImageUrl: '', bannerUrl: '', albumPhotos: [], areasAtuacao: [], referralCode: role === 'trabalhador' ? 'DEMOALEX' : 'DEMOBRASA', stripeAccountId: ''})),
    vagas: [{...common, _id: 'demo-vaga-1', userId: ids.restaurante, creator: ids.restaurante, titulo: 'Auxiliar de cozinha — vaga fictícia', cargo: 'auxiliar_cozinha', estabelecimentoNome: names.restaurante, localizacao: 'Milão — demonstração', tipoContrato: 'fixa', regimeTrabalho: 'full_time', horasSemanais: 40, valorHora: 12, descricao: 'Exemplo para testar candidatura. Aceita candidatos sem experiência. Não é uma oferta de emprego real.', ativa: true}],
    candidaturas: [],
    amizades: [{...common, _id: 'demo-amizade-1', solicitanteNome: names.trabalhador, destinatarioNome: names.restaurante, solicitanteId: ids.trabalhador, destinatarioId: ids.restaurante, status: 'aceito', creator: ids.trabalhador}],
    mensagens_chat: [],
    posts_feed: [{...common, _id: 'demo-post-1', userId: ids.restaurante, creator: ids.restaurante, userNome: names.restaurante, userFoto: '', conteudo: 'Bem-vindo à demonstração! Publicações e mensagens aqui ficam somente nesta aba.', imagens: [], curtidas: []}],
    comentarios_feed: [], marketplace_anuncios: [], curriculos: [],
  };
}
function matches(row: any, filter: Record<string, any> = {}): boolean {
  return Object.entries(filter).every(([key, expected]) => {
    if (key === '$or') return Array.isArray(expected) && expected.some(item => matches(row, item));
    if (key === '$and') return Array.isArray(expected) && expected.every(item => matches(row, item));
    if (expected && typeof expected === 'object' && !Array.isArray(expected)) {
      if ('$in' in expected) return expected.$in.includes(row[key]);
      if ('$ne' in expected) return row[key] !== expected.$ne;
      throw new Error('Filtro ainda não suportado na demonstração.');
    }
    return row[key] === expected;
  });
}
// Sem cliente real, token, fetch ou chamadas ao SDK neste adaptador.
export function createDemoClient(role: DemoRole, storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>) {
  if (!['trabalhador', 'restaurante'].includes(role)) throw new Error('Perfil de demonstração inválido.');
  const user = {userId: ids[role], userName: names[role], email: `${role}@example.invalid`, userRole: 'USER'};
  const initial = seed();
  const read = (): Record<string, any[]> => {
    const raw = storage.getItem(DEMO_DATA_KEY);
    if (!raw) return clone(initial);
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Object.keys(initial).some(key => !Array.isArray(parsed[key]))) throw new Error('Dados da demonstração inválidos. Use Reiniciar demonstração.');
    return parsed;
  };
  const write = (db: Record<string, any[]>) => storage.setItem(DEMO_DATA_KEY, JSON.stringify(db));
  const unsupported = async () => { throw new Error('Operação indisponível na demonstração. Nenhum serviço real foi acessado.'); };
  const visible = (name: string, row: any) => {
    if (name === 'curriculos') return row.userId === user.userId;
    if (name === 'candidaturas') return row.candidatoId === user.userId || row.estabelecimentoId === user.userId;
    if (name === 'mensagens_chat') return row.remetenteId === user.userId || row.destinatarioId === user.userId;
    return true;
  };
  const entities = Object.fromEntries(Object.keys(initial).map(name => [name, {
    async list(options: any = {}) {
      let list = read()[name].filter(row => visible(name, row) && matches(row, options.filter));
      if (options.sort) list.sort((a, b) => {
        for (const [field, direction] of Object.entries(options.sort)) {
          if (a[field] === b[field]) continue;
          return (a[field] > b[field] ? 1 : -1) * (direction === -1 ? -1 : 1);
        } return 0;
      });
      const total = list.length;
      list = list.slice(options.skip || 0, (options.skip || 0) + (options.limit ?? 100));
      return {list: clone(list), total};
    },
    async create(data: any) {
      const db = read();
      if (name === 'vagas' && role !== 'restaurante') throw new Error('Selecione o perfil de estabelecimento.');
      if (name === 'candidaturas') {
        if (role !== 'trabalhador' || data.candidatoId !== user.userId) throw new Error('Selecione o perfil de trabalhador.');
        if (db.candidaturas.some(row => row.vagaId === data.vagaId && row.candidatoId === user.userId)) throw new Error('Você já se candidatou.');
      }
      const record = {...clone(data), _id: `demo-${crypto.randomUUID()}`, creator: user.userId};
      db[name].push(record); write(db); return clone(record);
    },
    async update(id: string, data: any) {
      const db = read(); const index = db[name].findIndex(row => row._id === id && visible(name, row));
      if (index < 0) throw new Error('Registro de demonstração não encontrado.');
      db[name][index] = {...db[name][index], ...clone(data), _id: id}; write(db); return clone(db[name][index]);
    },
    async delete(id: string) {
      const db = read(); const row = db[name].find(item => item._id === id && visible(name, item));
      if (!row) throw new Error('Registro de demonstração não encontrado.');
      db[name] = db[name].filter(item => item._id !== id); write(db); return {};
    },
  }]));
  return {
    auth: { user, isAuthenticated: true, accessToken: '', authorization: '',
      onAuthChange: (_listener: unknown) => () => {},
      refreshUser: async () => clone(user),
      signOut: async () => { storage.removeItem(DEMO_ROLE_KEY); },
      signInWithPassword: unsupported, signUpWithPassword: unsupported,
      signInWithOAuth: unsupported, sendOtp: unsupported, resetPassword: unsupported,
    },
    entities: new Proxy(entities, {get(target, key: string) { if (!(key in target)) throw new Error('Entidade indisponível na demonstração.'); return target[key]; }}),
    functions: { invoke: unsupported },
    tools: {file: {upload: async (files: File[]) => Promise.all(files.map(file => new Promise<{fileUrl: string}>((resolve, reject) => {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 1024 * 1024) { reject(new Error('Na demonstração, use JPG, PNG ou WebP de até 1 MB.')); return; }
      const reader = new FileReader(); reader.onload = () => resolve({fileUrl: String(reader.result)}); reader.onerror = () => reject(new Error('Falha ao abrir imagem local.')); reader.readAsDataURL(file);
    }))) }},
  };
}
